'use client';

import { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  LuMapPin,
  LuDatabase,
  LuGlobe,
  LuRadar,
  LuMail,
  LuPhone,
  LuCalendar,
  LuLoader,
  LuCheck,
  LuArrowRight,
  LuX,
} from 'react-icons/lu';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/toaster';
import { Card, CardContent } from '@/components/ui/card';
import { useServiceForm } from '@/hooks/useServiceForm';
import { ServiceType } from '@prisma/client';
import { useUser } from '@clerk/nextjs';

const ServiceSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  email: Yup.string().email('Invalid email').required('Required'),
  phone: Yup.string().nullable(),
  serviceType: Yup.string().required('Required'),
  message: Yup.string(),
  preferredDate: Yup.date()
    .required('Required')
    .min(new Date(), 'Date cannot be in the past')
    .test(
      'is-weekday',
      'Please select a weekday (Monday to Friday)',
      function (value) {
        if (!value) return true; // Skip validation if no date provided
        const day = new Date(value).getDay();
        return day !== 0 && day !== 6; // 0 is Sunday, 6 is Saturday
      }
    ),
});

const GISMappingPage = () => {
  const { user, isSignedIn } = useUser();
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [activeService, setActiveService] = useState<number | null>(null);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(
    null
  );
  const { isSubmitting, submitServiceRequest } = useServiceForm();

  const services = [
    {
      icon: LuMapPin,
      title: 'GIS Data Collection',
      description: 'Professional GIS data collection and analysis services',
      features: [
        'Field data collection',
        'Data validation',
        'Spatial analysis',
        'Data processing',
      ],
      image: '/images/pages/gis-gis-data-collection.jpg',
      overlayColor: 'from-emerald-600/80',
    },
    {
      icon: LuDatabase,
      title: 'Spatial Analysis',
      description: 'Advanced spatial analysis and mapping solutions',
      features: [
        'Pattern analysis',
        'Spatial modeling',
        'Hot spot analysis',
        'Network analysis',
      ],
      image: '/images/pages/gis-spatial-analysis.jpg',
      overlayColor: 'from-blue-600/80',
    },
    {
      icon: LuGlobe,
      title: 'Remote Sensing',
      description: 'Remote sensing and satellite imagery interpretation',
      features: [
        'Satellite imagery',
        'Image classification',
        'Change detection',
        'Terrain analysis',
      ],
      image: '/images/pages/gis-remote-sensing.jpg',
      overlayColor: 'from-purple-600/80',
    },
    {
      icon: LuRadar,
      title: 'Digital Mapping',
      description: 'Digital mapping and cartography services',
      features: [
        'Thematic mapping',
        'Web mapping',
        'Custom cartography',
        'Interactive maps',
      ],
      image: '/images/pages/gis-digital-mapping.jpg',
      overlayColor: 'from-cyan-600/80',
    },
  ];

  // Define an interface for the form values
  interface FormValues {
    name: string;
    email: string;
    phone: string;
    serviceType: string;
    message: string;
    preferredDate: string;
    location?: string;
    projectSize?: string;
  }

  const handleSubmit = async (
    values: FormValues,
    { resetForm }: { resetForm: () => void }
  ) => {
    // Set initial status
    setSubmitStatus(null);

    try {
      // Show loading toast
      toast.loading('Submitting your request...');

      // Format message to include additional details
      const detailedMessage = `
Additional information: ${values.message || 'None provided'}
      `.trim();

      // Prepare the data for our service API
      const serviceData = {
        name: values.name,
        email: values.email,
        phone: values.phone || '',
        message: detailedMessage,
        serviceType: ServiceType.GIS,
        preferredDate: new Date(values.preferredDate),
        location: values.location || '',
        projectSize: values.projectSize || '',
        specificService: values.serviceType,
      };

      // Submit to service API using the new hook
      const result = await submitServiceRequest(serviceData);

      // Dismiss loading toast
      toast.dismiss();

      if (result.success) {
        setSubmitStatus('success');
        toast.success('GIS mapping request submitted successfully!');
        resetForm();
        setSelectedService(null);
      } else {
        setSubmitStatus('error');
        toast.error(
          result.error || 'Failed to submit request. Please try again.'
        );
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
      toast.dismiss();
      toast.error('Failed to submit request. Please try again.');
    }
  };

  return (
    <div className='min-h-screen bg-[#0f1f15]'>
      <Toaster position='top-center' />
      {/* Hero Section */}
      <div className='relative h-[40vh] md:h-[60vh] overflow-hidden'>
        <div className='absolute inset-0'>
          <img
            src='/images/pages/gis-hero.jpg'
            alt='GIS Mapping Services'
            className='h-full w-full object-cover'
          />
          <div className='absolute inset-0 bg-linear-to-b from-[#0a2213]/50 via-[#0a2213]/80 to-[#0a2213]' />
        </div>
        <div className='relative h-full mx-auto max-w-7xl px-4'>
          <div className='flex h-full flex-col items-center justify-center text-center'>
            <h1 className='text-3xl md:text-4xl lg:text-6xl font-bold text-white'>
              GIS & Mapping Solutions
            </h1>
            <p className='mt-3 md:mt-6 text-base md:text-xl text-emerald-100'>
              Advanced geospatial solutions powered by cutting-edge technology
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className='relative mx-auto max-w-7xl px-4 py-8 md:py-16'>
        {/* Contact Info Cards */}
        <div className='grid gap-3 md:gap-4 grid-cols-1 sm:grid-cols-3 mb-8 md:mb-16'>
          {[
            { icon: LuPhone, label: 'Phone', value: '+254 756 211 546' },
            { icon: LuMail, label: 'Email', value: 'gis@qwetulands.com' },
            { icon: LuCalendar, label: 'Hours', value: 'Mon-Fri: 8am - 6pm' },
          ].map((contact, index) => (
            <Card
              key={index}
              className='bg-emerald-900/30 border-emerald-700/50 text-white shadow-lg rounded-xl'
            >
              <CardContent className='p-4 flex flex-col items-center text-center'>
                <contact.icon className='h-8 w-8 text-emerald-400 mb-2' />
                <h4 className='font-semibold text-emerald-200'>
                  {contact.label}
                </h4>
                <p className='text-sm text-emerald-300'>{contact.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Services Section */}
        <h2 className='text-2xl md:text-3xl font-bold text-center text-emerald-100 mb-8 md:mb-12'>
          Our GIS & Mapping Expertise
        </h2>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-12 md:mb-20'>
          {services.map((service, idx) => (
            <div
              key={idx}
              className='group relative isolate overflow-hidden rounded-xl md:rounded-2xl transform transition-all duration-500 hover:scale-[1.02]'
              onMouseEnter={() => setActiveService(idx)}
              onMouseLeave={() => setActiveService(null)}
              onClick={() => setSelectedService(service.title)}
            >
              <div className='relative h-[250px] md:h-[400px] overflow-hidden rounded-xl md:rounded-2xl'>
                <img
                  src={service.image}
                  alt={service.title}
                  className='absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110 brightness-[0.85]'
                />

                <div
                  className={`absolute inset-0 bg-linear-to-b ${
                    service.overlayColor
                  } opacity-50 transition-opacity duration-500 ${
                    activeService === idx ? 'sm:opacity-70' : 'sm:opacity-0'
                  }`}
                />

                {/* Content Overlay */}
                <div className='absolute inset-0 flex flex-col justify-between p-4 md:p-8'>
                  <div className='flex items-center'>
                    <div className='rounded-lg bg-emerald-500/20 p-2 text-white'>
                      <service.icon className='h-5 w-5 md:h-6 md:w-6' />
                    </div>
                    <h3 className='ml-2 text-lg md:text-xl font-bold text-white'>
                      {service.title}
                    </h3>
                  </div>

                  <div
                    className={`transition-opacity duration-500 ${
                      activeService === idx
                        ? 'opacity-100'
                        : 'opacity-0 sm:hidden'
                    }`}
                  >
                    <p className='mb-2 md:mb-4 text-sm md:text-base text-white'>
                      {service.description}
                    </p>
                    <ul className='grid grid-cols-2 gap-1 md:gap-2'>
                      {service.features.map((feature, featureIdx) => (
                        <li
                          key={featureIdx}
                          className='flex items-center gap-1 md:gap-2'
                        >
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Service Request Form */}
        <div className='mx-auto max-w-4xl'>
          <Card className='overflow-hidden rounded-lg sm:rounded-xl md:rounded-3xl bg-linear-to-br from-emerald-950/90 to-emerald-900/90 backdrop-blur-xs shadow-xl border border-emerald-500/20'>
            <CardContent className='p-3 sm:p-4 md:p-8'>
              <div className='mb-3 sm:mb-6 md:mb-8 text-center'>
                <h2 className='text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-white'>
                  <span className='bg-linear-to-r from-emerald-400 via-green-400 to-teal-400 bg-clip-text text-transparent'>
                    Request GIS Mapping Services
                  </span>
                </h2>
                <p className='mt-1 sm:mt-2 text-xs sm:text-sm md:text-base text-emerald-300'>
                  Fill out the form below to request our GIS mapping services
                </p>
              </div>

              {selectedService ? (
                <Formik
                  initialValues={{
                    name: isSignedIn && user?.fullName ? user.fullName : '',
                    email:
                      isSignedIn && user?.primaryEmailAddress?.emailAddress
                        ? user.primaryEmailAddress.emailAddress
                        : '',
                    phone: '',
                    serviceType: selectedService || '',
                    message: '',
                    preferredDate: new Date().toISOString().split('T')[0],
                    location: '',
                    projectSize: '',
                  }}
                  validationSchema={ServiceSchema}
                  onSubmit={handleSubmit}
                  enableReinitialize
                >
                  {({ errors, touched, isValid, setFieldValue }) => (
                    <Form className='space-y-4 md:space-y-6'>
                      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6'>
                        <div>
                          <label
                            htmlFor='name'
                            className='mb-1.5 block text-sm font-medium text-emerald-200'
                          >
                            Full Name
                          </label>
                          <Field
                            name='name'
                            className='w-full rounded-lg border border-emerald-600/50 bg-[#0a2213]/60 p-3 text-sm text-emerald-50 placeholder-emerald-400/60 focus:border-emerald-500 focus:ring-3 focus:ring-emerald-500/30 transition'
                            placeholder='Your Full Name'
                            readOnly={isSignedIn}
                          />
                          {errors.name && touched.name ? (
                            <div className='mt-1.5 text-xs text-red-400'>
                              {errors.name}
                            </div>
                          ) : null}
                        </div>
                        <div>
                          <label
                            htmlFor='email'
                            className='mb-1.5 block text-sm font-medium text-emerald-200'
                          >
                            Email Address
                          </label>
                          <Field
                            name='email'
                            type='email'
                            className='w-full rounded-lg border border-emerald-600/50 bg-[#0a2213]/60 p-3 text-sm text-emerald-50 placeholder-emerald-400/60 focus:border-emerald-500 focus:ring-3 focus:ring-emerald-500/30 transition'
                            placeholder='your.email@example.com'
                            readOnly={isSignedIn}
                          />
                          {errors.email && touched.email ? (
                            <div className='mt-1.5 text-xs text-red-400'>
                              {errors.email}
                            </div>
                          ) : null}
                        </div>

                        <div>
                          <label
                            htmlFor='phone'
                            className='mb-1 md:mb-2 block text-xs md:text-sm font-medium text-emerald-300'
                          >
                            Phone Number
                          </label>
                          <Field
                            name='phone'
                            className='service-emerald-input'
                            placeholder='+254 7XX XXX XXX'
                          />
                          {errors.phone && touched.phone && (
                            <div className='mt-1 text-xs md:text-sm text-red-400'>
                              {errors.phone}
                            </div>
                          )}
                        </div>

                        <div>
                          <label
                            htmlFor='serviceType'
                            className='mb-1 md:mb-2 block text-xs md:text-sm font-medium text-emerald-300'
                          >
                            Service Type
                          </label>
                          <Field
                            as='select'
                            name='serviceType'
                            className='service-emerald-input'
                          >
                            {services.map((service) => (
                              <option
                                key={service.title}
                                value={service.title}
                                className='bg-emerald-900'
                              >
                                {service.title}
                              </option>
                            ))}
                          </Field>
                        </div>

                        <div>
                          <label
                            htmlFor='preferredDate'
                            className='mb-1 md:mb-2 block text-xs md:text-sm font-medium text-emerald-300'
                          >
                            Preferred Date
                          </label>
                          <Field
                            name='preferredDate'
                            type='date'
                            className='service-emerald-input'
                            min={new Date().toISOString().split('T')[0]}
                          />
                          {errors.preferredDate && touched.preferredDate && (
                            <div className='mt-1 text-xs md:text-sm text-red-400'>
                              {errors.preferredDate}
                            </div>
                          )}
                          <div className='mt-1 text-xs text-emerald-300'>
                            Service booking can only be effected on weekdays
                            (Monday-Friday)
                          </div>
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor='message'
                          className='mb-1 md:mb-2 block text-xs md:text-sm font-medium text-emerald-300'
                        >
                          Project Details
                        </label>
                        <Field
                          as='textarea'
                          name='message'
                          rows={3}
                          className='service-emerald-input'
                          placeholder='Tell us about your project requirements...'
                        />
                      </div>

                      <div className='flex justify-end'>
                        <button
                          type='button'
                          onClick={() => setSelectedService(null)}
                          className='mr-2 xs:mr-2.5 sm:mr-3 md:mr-4 rounded-md xs:rounded-lg border border-emerald-400 px-3 xs:px-3.5 sm:px-4 md:px-6 py-1.5 xs:py-2 sm:py-2 md:py-3 text-[11px] xs:text-xs md:text-sm text-emerald-300 transition-colors hover:bg-emerald-900/40 min-w-[60px] xs:min-w-[70px] sm:min-w-[80px]'
                        >
                          Cancel
                        </button>
                        <button
                          type='submit'
                          disabled={isSubmitting || !isValid}
                          className='flex items-center justify-center space-x-1 md:space-x-2 rounded-md xs:rounded-lg bg-linear-to-r from-emerald-600 to-green-600 px-3 xs:px-3.5 sm:px-4 md:px-6 py-1.5 xs:py-2 sm:py-2 md:py-3 text-[11px] xs:text-xs md:text-sm text-white transition-all hover:from-emerald-500 hover:to-green-500 disabled:opacity-70 hover:shadow-lg hover:shadow-emerald-500/20 hover:scale-105 min-w-[100px] xs:min-w-[120px] sm:min-w-[140px]'
                        >
                          {isSubmitting ? (
                            <>
                              <LuLoader className='h-4 w-4 md:h-5 md:w-5 animate-spin' />
                              <span>Submitting...</span>
                            </>
                          ) : (
                            <>
                              <span>Submit Request</span>
                              <LuArrowRight className='h-4 w-4 md:h-5 md:w-5' />
                            </>
                          )}
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              ) : (
                <div className='text-center'>
                  <p className='mb-4 md:mb-6 text-sm md:text-base text-emerald-300'>
                    Please select a service from the options above to request a
                    quote or consultation
                  </p>
                  <div className='grid gap-2 md:gap-4 grid-cols-2 sm:grid-cols-2 md:grid-cols-4'>
                    {services.map((service, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedService(service.title)}
                        className='group flex flex-col items-center rounded-xl bg-emerald-900/30 p-3 md:p-4 transition-all duration-300 hover:bg-emerald-800/40 hover:shadow-lg hover:shadow-emerald-500/10 hover:scale-105 border border-emerald-800/10 hover:border-emerald-700/30'
                      >
                        <div className='mb-2 md:mb-3 rounded-full bg-linear-to-br from-emerald-500 to-green-500 p-2 md:p-3 text-white transition-transform duration-300 group-hover:scale-110 group-hover:shadow-md group-hover:from-emerald-400 group-hover:to-green-400'>
                          <service.icon className='h-4 w-4 md:h-6 md:w-6' />
                        </div>
                        <span className='text-xs md:text-sm font-medium text-white'>
                          {service.title}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default GISMappingPage;
